import { useEffect, useMemo, useState } from "react";
import Dialog from "../../components/Dialog";
import { createCapsuleRequest } from "../../services/capsule/capsuleApi";
import {
  useLoaderData,
  useNavigate,
  useRouteLoaderData,
} from "react-router-dom";
import { Capsule } from "../../services/capsule/capsuleModel";
import CapsuleCard from "./capsule/CapsuleCardComponent";
import { toastsWithIntl } from "../../services/toastService";
import { User } from "../../services/user/userModel";
import { useConfirmationWithIntl } from "../../components/ConfirmationDialog";
import { capsuleService } from "../../services/capsule/capsuleService";
import { useTranslation } from "react-i18next";
import useReload from "../../hooks/useReload";
import CapsuleForm from "./capsule/CapsuleForm";
import CapsuleImageExample from "../../assets/img/2mm-ad-visual.jpg";
import {
  compareBoolean,
  compareDate,
  multiSorts,
  useSearch,
} from "../../react-helpers/array";

const OrganizationCompanyCapsules = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const reload = useReload();
  const { confirm } = useConfirmationWithIntl(["organization"]);
  const { toastError } = toastsWithIntl(["organization"]);
  const { archiveCapsule, unarchiveCapsule } = capsuleService();

  const { loggedUser } = useRouteLoaderData("logged-user") as {
    loggedUser: User;
  };
  const { capsules } = useLoaderData() as {
    capsules: Capsule[];
  };

  const [searchedCapsules, setSearch] = useSearch(capsules, (e) => ({
    name: e.name,
  }));

  const filteredCapsules = useMemo(() => {
    return searchedCapsules
      .filter((c) => c.archivedAt === null)
      .sort(
        multiSorts(
          // put disabled capsules at the end of the list
          (a, b) => compareBoolean(!a.disabledDate, !b.disabledDate),
          // most recent first
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedCapsules]);

  const archivedCapsules = useMemo(() => {
    return searchedCapsules
      .filter((c) => c.archivedAt !== null)
      .sort(
        multiSorts(
          (a, b) => compareBoolean(!a.disabledDate, !b.disabledDate),
          (a, b) => compareDate(new Date(b.createdAt), new Date(a.createdAt)),
        ),
      );
  }, [searchedCapsules]);

  const creditsRemaining = useMemo(
    () => loggedUser.organization!.maxCapsulesCount - capsules.length,
    [loggedUser.organization, capsules.length],
  );

  const [showArchived, setShowArchived] = useState(false);

  const [showNewCapsuleDialog, setShowNewCapsuleDialog] = useState(false);
  const createCapsulePromptKey = useMemo(() => {
    if (loggedUser.usage === "CALL_FOR_PROJECTS")
      return "organization:capsules.CREATE_CAPSULE_PROMPT_CALL_FOR_PROJECTS";
    if (
      loggedUser.usage === "EDUCATION" ||
      loggedUser.usage === "PROFESSIONAL_INTEGRATION"
    )
      return "organization:capsules.CREATE_CAPSULE_PROMPT_SUPPORT";
    return "organization:capsules.CREATE_CAPSULE_PROMPT";
  }, [loggedUser.usage]);

  useEffect(() => {
    if (showArchived && archivedCapsules.length === 0) {
      setShowArchived(false);
    }
  }, [archivedCapsules.length, showArchived]);

  return (
    <div className="layout_content --bg--organization-dashboard">
      <div className="ui-page pro-screen">
        <div className="ui-ph">
          <div className="ui-ph_t">
            <h1>{t("organization:capsules.YOUR_CAPSULES")}</h1>
            <p>{t(createCapsulePromptKey)}</p>
          </div>
          <div className="pro-row">
            {capsules.length < loggedUser.organization!.maxCapsulesCount && (
              <span className="ui-chip --ok --nod ui-num">
                {t("organization:capsules.REMAINING_CREDITS", {
                  credits: creditsRemaining,
                })}
              </span>
            )}
            {capsules.length > 0 && (
              <button
                className="btn"
                onClick={() => setShowNewCapsuleDialog(true)}
                disabled={
                  capsules.length >= loggedUser.organization!.maxCapsulesCount
                }
              >
                {t("organization:capsules.NEW_CAPSULE")}
              </button>
            )}
          </div>
        </div>

        {loggedUser.organization!.maxCapsulesCount > 0 &&
          capsules.length >= loggedUser.organization!.maxCapsulesCount && (
            <div className="ui-banner --warn">
              <span className="pro-ico --alert" aria-hidden="true" />
              <div className="ui-banner_grow">
                {t("organization:capsules.NO_CREDITS_LEFT")}
              </div>
            </div>
          )}
        {loggedUser.organization!.maxCapsulesCount == 0 && (
          <div className="ui-banner --warn">
            <span className="pro-ico --alert" aria-hidden="true" />
            <div className="ui-banner_grow">
              {t("organization:capsules.NO_CREDITS_AVAILABLE")}
            </div>
          </div>
        )}

        {creditsRemaining > 0 && capsules.length === 0 && (
          <div className="ui-card --pad pro-intro">
            <div>
              <div>{t("organization:capsules.BEFORE_YOU_START")}</div>
              <div>{t("organization:capsules.REPEAT_POSSIBILITY")}</div>
              <div>{t("organization:capsules.SHARING_LINK_UNCHANGED")}</div>
              <div>{t("organization:capsules.MORE_TIPS")}</div>
            </div>
            <div>
              <img src={CapsuleImageExample} alt="" />
            </div>
          </div>
        )}

        {archivedCapsules.length > 0 && (
          <div className="ui-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={!showArchived}
              onClick={() => setShowArchived(false)}
            >
              {t("organization:capsules.ACTIVE_CAPSULES")}
              <span className="ui-tabs_n">{filteredCapsules.length}</span>
            </button>
            <button
              role="tab"
              aria-selected={!!showArchived}
              onClick={() => setShowArchived(true)}
            >
              {t("organization:capsules.ARCHIVED_CAPSULES")}
              <span className="ui-tabs_n">{archivedCapsules.length}</span>
            </button>
          </div>
        )}

        {capsules.length > 0 && (
          <div className="pro-search">
            <span className="pro-ico --search" aria-hidden="true" />
            <input
              type="search"
              className="input"
              placeholder={t("organization:capsules.SEARCH_CAPSULE")}
              aria-label={t("organization:capsules.SEARCH_CAPSULE")}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        )}

        {capsules.length > 0 && (
          <div className="pro-grid">
            {!showArchived &&
              filteredCapsules.map((capsule) => (
                <CapsuleCard
                  key={capsule.id}
                  capsule={capsule}
                  isArchived={!!capsule.archivedAt}
                  onArchive={() => {
                    confirm(
                      "organization:capsule.archive.CONFIRM",
                      () =>
                        archiveCapsule(capsule.id).then(() => {
                          reload();
                        }),
                      true,
                    );
                  }}
                />
              ))}
            {!!showArchived &&
              archivedCapsules.map((capsule) => (
                <CapsuleCard
                  key={capsule.id}
                  capsule={capsule}
                  isArchived={!!capsule.archivedAt}
                  onUnarchive={() => {
                    confirm(
                      "organization:capsule.unarchive.CONFIRM",
                      () =>
                        unarchiveCapsule(capsule.id).then(() => {
                          reload();
                        }),
                      true,
                    );
                  }}
                />
              ))}
          </div>
        )}

        {capsules.length === 0 &&
          loggedUser.organization!.maxCapsulesCount > 0 && (
            <div className="ui-card pro-empty">
              <span className="pro-empty_ill">
                <span className="pro-ico --play" aria-hidden="true" />
              </span>
              <b>{t("organization:capsules.NO_CAPSULES_YET")}</b>
              <button
                className="btn --force"
                onClick={() => setShowNewCapsuleDialog(true)}
                disabled={
                  capsules.length >= loggedUser.organization!.maxCapsulesCount
                }
              >
                {t("organization:capsules.CREATE_FIRST_CAPSULE")}
              </button>
            </div>
          )}

        {showNewCapsuleDialog && (
          <Dialog
            className="--branded"
            onClose={() => setShowNewCapsuleDialog(false)}
          >
            <>
              <div className="popup_head">
                <div className="popup_title">
                  {t("organization:create-capsule.NEW_CAPSULE_DIALOG_TITLE")}
                </div>
              </div>

              <CapsuleForm
                onSubmit={(values) => {
                  return createCapsuleRequest(values).then(
                    (capsule) => {
                      return navigate(`/organization/capsules/${capsule.id}`);
                    },
                    (err) => {
                      if (err.response?.status === 412) {
                        toastError("organization:create-capsule.MAX_CAPSULES");
                        reload();
                        setShowNewCapsuleDialog(false);
                      }
                    },
                  );
                }}
                onCancel={() => setShowNewCapsuleDialog(false)}
              />
            </>
          </Dialog>
        )}
      </div>
    </div>
  );
};

export default OrganizationCompanyCapsules;
